import Phaser from 'phaser'
import {
  diamondPoints,
  fillPolygon,
  regularPolygon,
  strokePolygon,
  trianglePoints
} from './drawShapes'
import { strokeWidthForCell } from './scale'
import { THEME } from './theme'
import type { EnemyVisualDefinition, TowerVisualDefinition } from './types'

/** Draw tower footprint platform + silhouette into graphics at local 0,0 center. No letter labels. */
export function paintTowerVisual(
  g: Phaser.GameObjects.Graphics,
  visual: TowerVisualDefinition,
  level: number,
  footprintW: number,
  footprintH: number,
  cellSize: number,
  options?: { selected?: boolean }
): void {
  g.clear()
  const w = footprintW
  const h = footprintH
  const stroke = strokeWidthForCell(cellSize, 'thin')
  const bold = strokeWidthForCell(cellSize, 'medium')
  const heavy = strokeWidthForCell(cellSize, 'bold')
  const lv = Math.max(1, Math.min(4, level))
  const selected = options?.selected === true
  const corner = Math.max(2, cellSize * 0.1)

  // Shared DNA: dark platform + raised pad
  g.fillStyle(0x0b1220, 0.72)
  g.fillRoundedRect(-w / 2 + 1, -h / 2 + 1, w - 2, h - 2, corner)
  g.lineStyle(selected ? heavy : stroke, selected ? THEME.selection : visual.stroke, selected ? 1 : 0.45)
  g.strokeRoundedRect(-w / 2 + 1, -h / 2 + 1, w - 2, h - 2, corner)

  const pad = Math.min(w, h) * 0.42
  g.fillStyle(0x1e293b, selected ? 0.98 : 0.9)
  g.fillCircle(0, 0, pad)
  g.lineStyle(selected ? bold : stroke, selected ? THEME.selection : visual.stroke, selected ? 0.95 : 0.35)
  g.strokeCircle(0, 0, pad)
  if (selected) {
    g.lineStyle(stroke, THEME.selection, 0.55)
    g.strokeCircle(0, 0, pad * 1.12)
  }

  // LV adds modules — radius grows only slightly so structure carries the read
  const r = Math.min(w, h) * (0.26 + lv * 0.022)

  switch (visual.silhouette) {
    case 'triangle': {
      // FIRE — directional emitter △; fins / tip emitters / containment (not just glow)
      const pts = trianglePoints(0, 0, r)
      g.fillStyle(visual.fill, 0.96)
      fillPolygon(g, pts)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, pts)
      g.fillStyle(THEME.burnMark, 0.92)
      g.fillCircle(0, r * 0.12, r * (lv >= 3 ? 0.3 : 0.24))
      g.fillStyle(THEME.white, 0.38)
      g.fillCircle(-r * 0.08, r * 0.04, r * 0.09)
      if (lv >= 2) {
        const finL = [
          { x: -r * 0.52, y: r * 0.18 },
          { x: -r * 1.08, y: r * 0.52 },
          { x: -r * 0.42, y: r * 0.58 }
        ]
        const finR = [
          { x: r * 0.52, y: r * 0.18 },
          { x: r * 1.08, y: r * 0.52 },
          { x: r * 0.42, y: r * 0.58 }
        ]
        g.fillStyle(THEME.burnMark, 0.8)
        fillPolygon(g, finL)
        fillPolygon(g, finR)
        g.lineStyle(stroke, visual.stroke, 0.75)
        strokePolygon(g, finL)
        strokePolygon(g, finR)
      }
      if (lv >= 3) {
        g.lineStyle(stroke, THEME.burnMark, 0.8)
        g.strokeCircle(0, 0, r * 1.16)
        g.fillStyle(THEME.burnMark, 0.9)
        g.fillCircle(0, -r * 0.7, r * 0.13)
        g.lineStyle(stroke, visual.stroke, 0.9)
        g.strokeCircle(0, -r * 0.7, r * 0.13)
      }
      if (lv >= 4) {
        g.fillStyle(visual.fill, 0.92)
        fillPolygon(g, trianglePoints(-r * 0.4, -r * 0.52, r * 0.3))
        fillPolygon(g, trianglePoints(r * 0.4, -r * 0.52, r * 0.3))
        g.lineStyle(stroke, visual.stroke, 0.9)
        strokePolygon(g, trianglePoints(-r * 0.4, -r * 0.52, r * 0.3))
        strokePolygon(g, trianglePoints(r * 0.4, -r * 0.52, r * 0.3))
        g.lineStyle(bold, THEME.burnMark, 0.95)
        g.strokeCircle(0, 0, r * 1.3)
        g.fillStyle(THEME.white, 0.55)
        g.fillCircle(0, r * 0.12, r * 0.1)
      }
      break
    }
    case 'hexagon': {
      // FROST — crystal hex (facets / shards / rings) — not a blue fire triangle
      const outer = regularPolygon(0, 0, r, 6)
      g.fillStyle(visual.fill, 0.94)
      fillPolygon(g, outer)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, outer)
      // Inner ice facet (hex, not circle — crystal DNA)
      g.fillStyle(THEME.frostMark, 0.5)
      fillPolygon(g, regularPolygon(0, 0, r * 0.48, 6))
      g.fillStyle(THEME.deepFreeze, 0.35)
      fillPolygon(g, regularPolygon(0, 0, r * 0.22, 6))
      if (lv >= 2) {
        g.lineStyle(stroke, THEME.white, 0.6)
        for (let i = 0; i < 6; i += 1) {
          const a = -Math.PI / 2 + (i * Math.PI * 2) / 6
          g.lineBetween(0, 0, Math.cos(a) * r * 0.88, Math.sin(a) * r * 0.88)
        }
        // Vertex crystal pips
        g.fillStyle(THEME.deepFreeze, 0.85)
        for (let i = 0; i < 6; i += 2) {
          const a = -Math.PI / 2 + (i * Math.PI * 2) / 6
          g.fillCircle(Math.cos(a) * r * 0.92, Math.sin(a) * r * 0.92, r * 0.08)
        }
      }
      if (lv >= 3) {
        // Side crystal shards (angular — distinct from FIRE heat fins)
        for (let i = 0; i < 6; i += 2) {
          const a = Math.PI / 6 + (i * Math.PI * 2) / 6
          const ox = Math.cos(a) * r * 1.05
          const oy = Math.sin(a) * r * 1.05
          const shard = regularPolygon(ox, oy, r * 0.22, 4, a)
          g.fillStyle(THEME.frostMark, 0.75)
          fillPolygon(g, shard)
          g.lineStyle(stroke, visual.stroke, 0.7)
          strokePolygon(g, shard)
        }
        g.lineStyle(stroke, THEME.frostMark, 0.75)
        strokePolygon(g, regularPolygon(0, 0, r * 1.2, 6))
      }
      if (lv >= 4) {
        g.fillStyle(THEME.deepFreeze, 0.7)
        fillPolygon(g, regularPolygon(0, 0, r * 0.18, 6))
        g.lineStyle(bold, THEME.deepFreeze, 0.95)
        strokePolygon(g, regularPolygon(0, 0, r * 1.34, 6))
        // Apex crystal tip
        g.fillStyle(THEME.white, 0.85)
        fillPolygon(g, diamondPoints(0, -r * 1.05, r * 0.12, r * 0.22))
      }
      break
    }
    case 'block': {
      // CANNON — mass block + bore (geometric energy mass, not military realism)
      const s = r * 1.18
      const rad = Math.max(1, cellSize * 0.04)
      g.fillStyle(visual.fill, 0.96)
      g.fillRoundedRect(-s * 0.55, -s * 0.55, s * 1.1, s * 1.1, rad)
      g.lineStyle(bold, visual.stroke, 0.95)
      g.strokeRoundedRect(-s * 0.55, -s * 0.55, s * 1.1, s * 1.1, rad)
      // Mass highlight stripe
      g.fillStyle(THEME.white, 0.18)
      g.fillRect(-s * 0.1, -s * 0.55, s * 0.2, s * 1.1)
      // Bore: rim + dark aperture
      g.fillStyle(0x64748b, 0.95)
      g.fillCircle(0, 0, s * 0.3)
      g.lineStyle(stroke, THEME.white, 0.9)
      g.strokeCircle(0, 0, s * 0.3)
      g.fillStyle(THEME.background, 0.98)
      g.fillCircle(0, 0, s * 0.14)
      if (lv >= 2) {
        g.lineStyle(stroke, THEME.white, 0.45)
        g.strokeRoundedRect(-s * 0.72, -s * 0.72, s * 1.44, s * 1.44, rad)
        g.fillStyle(0x475569, 0.7)
        g.fillRoundedRect(-s * 0.22, -s * 0.72, s * 0.44, s * 0.14, 1)
      }
      if (lv >= 3) {
        // Side mass modules
        g.fillStyle(visual.fill, 0.9)
        g.fillRoundedRect(-s * 0.92, -s * 0.2, s * 0.28, s * 0.4, rad)
        g.fillRoundedRect(s * 0.64, -s * 0.2, s * 0.28, s * 0.4, rad)
        g.lineStyle(stroke, visual.stroke, 0.85)
        g.strokeRoundedRect(-s * 0.92, -s * 0.2, s * 0.28, s * 0.4, rad)
        g.strokeRoundedRect(s * 0.64, -s * 0.2, s * 0.28, s * 0.4, rad)
      }
      if (lv >= 4) {
        // Dual bore rings + geometric muzzle wedge
        g.lineStyle(bold, THEME.white, 0.75)
        g.strokeCircle(0, 0, s * 0.48)
        g.strokeCircle(0, 0, s * 0.62)
        const muzzle = [
          { x: -s * 0.18, y: -s * 0.55 },
          { x: 0, y: -s * 0.95 },
          { x: s * 0.18, y: -s * 0.55 }
        ]
        g.fillStyle(0x64748b, 0.9)
        fillPolygon(g, muzzle)
        g.lineStyle(stroke, visual.stroke, 0.9)
        strokePolygon(g, muzzle)
      }
      break
    }
    case 'diamond': {
      // POISON — diamond dart (pointed toxin tip), not a green blob
      const pts = diamondPoints(0, 0, r * 0.62, r * 1.05)
      g.fillStyle(visual.fill, 0.94)
      fillPolygon(g, pts)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, pts)
      // Tip accent + mid core (dart read)
      g.fillStyle(THEME.poisonMark, 0.85)
      fillPolygon(g, diamondPoints(0, -r * 0.55, r * 0.22, r * 0.32))
      g.fillStyle(THEME.labelDark, 0.45)
      g.fillCircle(0, r * 0.08, r * 0.16)
      if (lv >= 2) {
        // Fletching wings — secondary geometry
        const wingL = [
          { x: -r * 0.2, y: r * 0.15 },
          { x: -r * 0.95, y: r * 0.35 },
          { x: -r * 0.25, y: r * 0.55 }
        ]
        const wingR = [
          { x: r * 0.2, y: r * 0.15 },
          { x: r * 0.95, y: r * 0.35 },
          { x: r * 0.25, y: r * 0.55 }
        ]
        g.fillStyle(THEME.poisonMark, 0.75)
        fillPolygon(g, wingL)
        fillPolygon(g, wingR)
        g.lineStyle(stroke, visual.stroke, 0.7)
        strokePolygon(g, wingL)
        strokePolygon(g, wingR)
      }
      if (lv >= 3) {
        g.fillStyle(THEME.poisonMark, 0.9)
        g.fillCircle(-r * 0.55, 0, r * 0.12)
        g.fillCircle(r * 0.55, 0, r * 0.12)
        g.lineStyle(stroke, visual.stroke, 0.8)
        g.strokeCircle(-r * 0.55, 0, r * 0.12)
        g.strokeCircle(r * 0.55, 0, r * 0.12)
      }
      if (lv >= 4) {
        g.lineStyle(bold, THEME.poisonMark, 0.85)
        g.strokeCircle(0, 0, r * 1.28)
        g.fillStyle(THEME.poisonMark, 0.95)
        g.fillCircle(0, -r * 0.95, r * 0.11)
        g.fillCircle(0, r * 0.95, r * 0.11)
        g.fillStyle(THEME.white, 0.45)
        g.fillCircle(0, r * 0.08, r * 0.1)
      }
      break
    }
    case 'antenna': {
      // AA — twin prongs + tracker core (lock rings at higher LV)
      const base = r * 0.55
      g.fillStyle(visual.fill, 0.92)
      g.fillRoundedRect(-base * 0.85, base * 0.2, base * 1.7, base * 0.65, Math.max(1, cellSize * 0.03))
      g.lineStyle(bold, visual.stroke, 0.95)
      g.strokeRoundedRect(-base * 0.85, base * 0.2, base * 1.7, base * 0.65, Math.max(1, cellSize * 0.03))
      // Twin prongs (thick geometric beams, not thin lines alone)
      const prongL = [
        { x: -base * 0.55, y: base * 0.25 },
        { x: -base * 0.75, y: -r * 0.95 },
        { x: -base * 0.35, y: -r * 0.95 },
        { x: -base * 0.2, y: base * 0.25 }
      ]
      const prongR = [
        { x: base * 0.2, y: base * 0.25 },
        { x: base * 0.35, y: -r * 0.95 },
        { x: base * 0.75, y: -r * 0.95 },
        { x: base * 0.55, y: base * 0.25 }
      ]
      g.fillStyle(visual.fill, 0.95)
      fillPolygon(g, prongL)
      fillPolygon(g, prongR)
      g.lineStyle(stroke, visual.stroke, 0.9)
      strokePolygon(g, prongL)
      strokePolygon(g, prongR)
      g.fillStyle(THEME.white, 0.95)
      g.fillCircle(-base * 0.55, -r * 0.95, r * 0.11)
      g.fillCircle(base * 0.55, -r * 0.95, r * 0.11)
      // Tracker diamond
      g.fillStyle(visual.fill, 0.98)
      fillPolygon(g, diamondPoints(0, -r * 0.05, r * 0.26, r * 0.36))
      g.lineStyle(stroke, visual.stroke, 0.9)
      strokePolygon(g, diamondPoints(0, -r * 0.05, r * 0.26, r * 0.36))
      if (lv >= 2) {
        g.lineStyle(stroke, THEME.white, 0.65)
        g.strokeCircle(0, -r * 0.05, r * 0.52)
      }
      if (lv >= 3) {
        // Center dish / third tracker spur
        g.fillStyle(THEME.white, 0.75)
        g.fillRoundedRect(-r * 0.08, -r * 1.05, r * 0.16, r * 0.85, 1)
        g.fillStyle(visual.fill, 0.9)
        g.fillCircle(0, -r * 1.05, r * 0.12)
        g.lineStyle(stroke, visual.stroke, 0.85)
        g.strokeCircle(0, -r * 1.05, r * 0.12)
      }
      if (lv >= 4) {
        g.lineStyle(bold, THEME.white, 0.85)
        g.strokeCircle(0, -r * 0.05, r * 0.82)
        g.fillStyle(THEME.white, 0.55)
        g.fillCircle(0, -r * 0.05, r * 0.12)
      }
      break
    }
    case 'prism': {
      // MAGE — fracture prism (geometric shatter), no staff / wizard crystal tower
      const outer = regularPolygon(0, 0, r, 5)
      g.fillStyle(visual.fill, 0.94)
      fillPolygon(g, outer)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, outer)
      // Fracture core — inverted triangle chip
      g.fillStyle(THEME.armorBreakMark, 0.65)
      fillPolygon(g, regularPolygon(0, r * 0.05, r * 0.4, 3, Math.PI / 2))
      g.fillStyle(THEME.white, 0.5)
      g.fillCircle(0, 0, r * 0.14)
      if (lv >= 2) {
        // Offset fracture shell (second outline, slightly rotated)
        g.lineStyle(stroke, THEME.armorBreakMark, 0.7)
        strokePolygon(g, regularPolygon(0, 0, r * 1.14, 5, -Math.PI / 2 + 0.12))
      }
      if (lv >= 3) {
        g.lineStyle(stroke, THEME.white, 0.55)
        for (let i = 0; i < 5; i += 1) {
          const a = -Math.PI / 2 + (i * Math.PI * 2) / 5
          g.lineBetween(0, 0, Math.cos(a) * r * 0.88, Math.sin(a) * r * 0.88)
        }
        // Corner fracture chips
        for (let i = 0; i < 5; i += 2) {
          const a = -Math.PI / 2 + (i * Math.PI * 2) / 5
          g.fillStyle(THEME.armorBreakMark, 0.8)
          fillPolygon(
            g,
            regularPolygon(Math.cos(a) * r * 1.05, Math.sin(a) * r * 1.05, r * 0.16, 3, a)
          )
        }
      }
      if (lv >= 4) {
        // Broken outer ring segments (signature shatter) — not a solid fantasy crystal
        g.lineStyle(bold, THEME.armorBreakMark, 0.9)
        const shell = regularPolygon(0, 0, r * 1.32, 5)
        for (let i = 0; i < shell.length; i += 1) {
          if (i % 2 === 1) continue
          const a = shell[i]!
          const b = shell[(i + 1) % shell.length]!
          g.lineBetween(a.x, a.y, b.x, b.y)
        }
        g.fillStyle(THEME.white, 0.7)
        g.fillCircle(0, 0, r * 0.12)
      }
      break
    }
    default: {
      g.fillStyle(visual.fill, 0.95)
      g.fillCircle(0, 0, r)
      g.lineStyle(stroke, visual.stroke, 0.95)
      g.strokeCircle(0, 0, r)
    }
  }
}

export function paintEnemyVisual(
  g: Phaser.GameObjects.Graphics,
  visual: EnemyVisualDefinition,
  radius: number,
  cellSize: number,
  options?: { hasShield?: boolean; regenerating?: boolean; elite?: boolean }
): void {
  g.clear()
  const stroke = strokeWidthForCell(cellSize, 'thin')
  const bold = strokeWidthForCell(cellSize, 'medium')
  const heavy = strokeWidthForCell(cellSize, 'bold')
  const corner = Math.max(1, cellSize * 0.04)

  switch (visual.silhouette) {
    case 'triangle': {
      // FAST — forward chevron + speed notch (reads ≠ NORMAL even stationary)
      const body = [
        { x: 0, y: -radius * 1.05 },
        { x: radius * 0.92, y: radius * 0.55 },
        { x: radius * 0.28, y: radius * 0.22 },
        { x: 0, y: radius * 0.72 },
        { x: -radius * 0.28, y: radius * 0.22 },
        { x: -radius * 0.92, y: radius * 0.55 }
      ]
      g.fillStyle(visual.fill, 0.96)
      fillPolygon(g, body)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, body)
      // Speed notch / wake cut
      g.fillStyle(THEME.background, 0.55)
      fillPolygon(g, [
        { x: 0, y: radius * 0.05 },
        { x: radius * 0.22, y: radius * 0.55 },
        { x: 0, y: radius * 0.42 },
        { x: -radius * 0.22, y: radius * 0.55 }
      ])
      g.fillStyle(THEME.white, 0.45)
      g.fillCircle(0, -radius * 0.35, radius * 0.16)
      g.lineStyle(stroke, THEME.white, 0.55)
      g.lineBetween(-radius * 0.5, radius * 0.15, 0, -radius * 0.55)
      g.lineBetween(radius * 0.5, radius * 0.15, 0, -radius * 0.55)
      break
    }
    case 'square': {
      // TANK — mass square shell + rivet plates (≠ GUARDIAN hex plates)
      const s = radius * 1.58
      g.fillStyle(0x1e293b, 0.85)
      g.fillRoundedRect(-s * 0.58, -s * 0.58, s * 1.16, s * 1.16, corner)
      g.fillStyle(visual.fill, 0.96)
      g.fillRoundedRect(-s / 2, -s / 2, s, s, corner)
      g.lineStyle(bold, visual.stroke, 0.95)
      g.strokeRoundedRect(-s / 2, -s / 2, s, s, corner)
      // Armor plate inset
      g.fillStyle(0x4c3d78, 0.75)
      g.fillRect(-s * 0.32, -s * 0.32, s * 0.64, s * 0.64)
      g.lineStyle(stroke, THEME.white, 0.45)
      g.strokeRect(-s * 0.32, -s * 0.32, s * 0.64, s * 0.64)
      // Corner rivets — square mass identity
      g.fillStyle(THEME.white, 0.7)
      const rivet = radius * 0.1
      g.fillCircle(-s * 0.38, -s * 0.38, rivet)
      g.fillCircle(s * 0.38, -s * 0.38, rivet)
      g.fillCircle(-s * 0.38, s * 0.38, rivet)
      g.fillCircle(s * 0.38, s * 0.38, rivet)
      g.fillStyle(THEME.background, 0.5)
      g.fillCircle(0, 0, radius * 0.22)
      break
    }
    case 'diamond': {
      // REGEN — tall diamond + inner pulse core
      const pts = diamondPoints(0, 0, radius * 0.78, radius * 1.08)
      g.fillStyle(visual.fill, 0.95)
      fillPolygon(g, pts)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, pts)
      g.fillStyle(THEME.background, 0.45)
      fillPolygon(g, diamondPoints(0, 0, radius * 0.38, radius * 0.52))
      g.fillStyle(THEME.regenEnemy, 0.95)
      g.fillCircle(0, 0, radius * 0.28)
      g.lineStyle(stroke, THEME.white, 0.85)
      g.strokeCircle(0, 0, radius * 0.28)
      g.fillStyle(THEME.white, 0.5)
      g.fillCircle(-radius * 0.08, -radius * 0.08, radius * 0.1)
      break
    }
    case 'hexagon': {
      // SHIELD — core + visible separate shell layer (break state uses hasShield ring)
      const core = regularPolygon(0, 0, radius * 0.72, 6)
      g.fillStyle(visual.fill, 0.96)
      fillPolygon(g, core)
      g.lineStyle(stroke, visual.stroke, 0.9)
      strokePolygon(g, core)
      g.fillStyle(THEME.background, 0.4)
      g.fillCircle(0, 0, radius * 0.28)
      // Separate armor shell (always present — family DNA)
      const shell = regularPolygon(0, 0, radius * 1.08, 6)
      g.lineStyle(bold, THEME.shield, 0.95)
      strokePolygon(g, shell)
      g.lineStyle(stroke, THEME.white, 0.4)
      strokePolygon(g, regularPolygon(0, 0, radius * 1.18, 6))
      // Shell facet pips
      for (let i = 0; i < 6; i += 2) {
        const a = -Math.PI / 2 + (i * Math.PI * 2) / 6
        g.fillStyle(THEME.shield, 0.75)
        g.fillCircle(Math.cos(a) * radius * 1.08, Math.sin(a) * radius * 1.08, radius * 0.09)
      }
      break
    }
    case 'boss': {
      // BOSS — heavy ground octagon fortress (≠ AIR_BOSS winged mass)
      g.fillStyle(0x1e293b, 0.9)
      fillPolygon(g, regularPolygon(0, 0, radius * 1.08, 8))
      g.fillStyle(visual.fill, 0.94)
      fillPolygon(g, regularPolygon(0, 0, radius, 8))
      g.lineStyle(heavy, visual.stroke, 0.95)
      strokePolygon(g, regularPolygon(0, 0, radius, 8))
      g.lineStyle(stroke, THEME.selection, 0.85)
      strokePolygon(g, regularPolygon(0, 0, radius * 0.78, 8))
      g.fillStyle(0x1e293b, 0.88)
      fillPolygon(g, regularPolygon(0, 0, radius * 0.5, 6))
      g.lineStyle(bold, THEME.selection, 0.95)
      strokePolygon(g, regularPolygon(0, 0, radius * 0.5, 6))
      g.fillStyle(THEME.selection, 0.65)
      g.fillCircle(0, 0, radius * 0.22)
      // Crown spikes — ground boss signature
      for (let i = 0; i < 4; i += 1) {
        const a = -Math.PI / 2 + (i * Math.PI) / 2
        const tip = [
          { x: Math.cos(a) * radius * 0.85, y: Math.sin(a) * radius * 0.85 },
          {
            x: Math.cos(a - 0.18) * radius * 1.22,
            y: Math.sin(a - 0.18) * radius * 1.22
          },
          {
            x: Math.cos(a + 0.18) * radius * 1.22,
            y: Math.sin(a + 0.18) * radius * 1.22
          }
        ]
        g.fillStyle(visual.fill, 0.95)
        fillPolygon(g, tip)
        g.lineStyle(stroke, visual.stroke, 0.9)
        strokePolygon(g, tip)
      }
      break
    }
    case 'air_boss': {
      // AIR_BOSS — twin-wing fortress + crown core (≠ scaled FLYER)
      const hull = [
        { x: 0, y: -radius * 0.95 },
        { x: radius * 0.42, y: -radius * 0.25 },
        { x: radius * 0.35, y: radius * 0.55 },
        { x: 0, y: radius * 0.75 },
        { x: -radius * 0.35, y: radius * 0.55 },
        { x: -radius * 0.42, y: -radius * 0.25 }
      ]
      const wingL = [
        { x: -radius * 0.35, y: -radius * 0.05 },
        { x: -radius * 1.25, y: radius * 0.05 },
        { x: -radius * 1.15, y: radius * 0.45 },
        { x: -radius * 0.28, y: radius * 0.35 }
      ]
      const wingR = [
        { x: radius * 0.35, y: -radius * 0.05 },
        { x: radius * 1.25, y: radius * 0.05 },
        { x: radius * 1.15, y: radius * 0.45 },
        { x: radius * 0.28, y: radius * 0.35 }
      ]
      g.fillStyle(0x0f2a44, 0.9)
      fillPolygon(g, wingL)
      fillPolygon(g, wingR)
      g.lineStyle(stroke, visual.stroke, 0.75)
      strokePolygon(g, wingL)
      strokePolygon(g, wingR)
      g.fillStyle(visual.fill, 0.96)
      fillPolygon(g, hull)
      g.lineStyle(heavy, visual.stroke, 0.95)
      strokePolygon(g, hull)
      // Crown core (boss weight without sharing ground octagon)
      g.fillStyle(THEME.selection, 0.55)
      fillPolygon(g, regularPolygon(0, -radius * 0.1, radius * 0.38, 6))
      g.lineStyle(bold, THEME.selection, 0.95)
      strokePolygon(g, regularPolygon(0, -radius * 0.1, radius * 0.38, 6))
      g.fillStyle(THEME.white, 0.55)
      g.fillCircle(0, -radius * 0.1, radius * 0.14)
      // Twin thruster nodes
      g.fillStyle(THEME.white, 0.7)
      g.fillCircle(-radius * 0.22, radius * 0.48, radius * 0.1)
      g.fillCircle(radius * 0.22, radius * 0.48, radius * 0.1)
      break
    }
    case 'flyer': {
      // FLYER — light winged delta (AIR; shadow supplied by scene)
      const wing = [
        { x: -radius * 1.08, y: radius * 0.22 },
        { x: -radius * 0.2, y: -radius * 0.12 },
        { x: 0, y: -radius * 0.95 },
        { x: radius * 0.2, y: -radius * 0.12 },
        { x: radius * 1.08, y: radius * 0.22 },
        { x: radius * 0.32, y: radius * 0.48 },
        { x: 0, y: radius * 0.12 },
        { x: -radius * 0.32, y: radius * 0.48 }
      ]
      g.fillStyle(visual.fill, 0.95)
      fillPolygon(g, wing)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, wing)
      g.fillStyle(THEME.white, 0.4)
      g.fillCircle(0, -radius * 0.22, radius * 0.16)
      g.lineStyle(stroke, THEME.white, 0.45)
      g.lineBetween(-radius * 0.55, radius * 0.05, 0, -radius * 0.55)
      g.lineBetween(radius * 0.55, radius * 0.05, 0, -radius * 0.55)
      break
    }
    case 'guardian': {
      // GUARDIAN — layered hex plates + crest (≠ TANK square mass)
      const outer = regularPolygon(0, 0, radius * 1.05, 6)
      g.fillStyle(visual.fill, 0.95)
      fillPolygon(g, outer)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, outer)
      g.fillStyle(0x64748b, 0.88)
      fillPolygon(g, regularPolygon(0, 0, radius * 0.7, 6))
      g.lineStyle(stroke, THEME.white, 0.55)
      strokePolygon(g, regularPolygon(0, 0, radius * 0.7, 6))
      // Vertical crest plate (guardian identity)
      const plate = [
        { x: -radius * 0.28, y: -radius * 0.55 },
        { x: radius * 0.28, y: -radius * 0.55 },
        { x: radius * 0.38, y: radius * 0.15 },
        { x: 0, y: radius * 0.55 },
        { x: -radius * 0.38, y: radius * 0.15 }
      ]
      g.fillStyle(0xcbd5e1, 0.9)
      fillPolygon(g, plate)
      g.lineStyle(stroke, THEME.white, 0.95)
      strokePolygon(g, plate)
      g.fillStyle(THEME.background, 0.45)
      g.fillCircle(0, -radius * 0.05, radius * 0.16)
      break
    }
    case 'phase': {
      // PHASE — offset twin pentagons (CONTROL-resistant)
      const a = regularPolygon(-radius * 0.22, -radius * 0.1, radius * 0.78, 5)
      const b = regularPolygon(radius * 0.24, radius * 0.12, radius * 0.82, 5)
      g.fillStyle(visual.fill, 0.4)
      fillPolygon(g, a)
      g.lineStyle(stroke, visual.stroke, 0.5)
      strokePolygon(g, a)
      g.fillStyle(visual.fill, 0.9)
      fillPolygon(g, b)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, b)
      g.fillStyle(THEME.white, 0.28)
      g.fillCircle(radius * 0.1, 0, radius * 0.2)
      g.lineStyle(stroke, THEME.white, 0.35)
      g.strokeCircle(-radius * 0.15, -radius * 0.08, radius * 0.55)
      break
    }
    case 'gem': {
      // CRYSTAL — faceted gem (TOXIC-resistant)
      const outer = [
        { x: 0, y: -radius * 1.05 },
        { x: radius * 0.75, y: -radius * 0.18 },
        { x: radius * 0.52, y: radius * 0.72 },
        { x: 0, y: radius * 1.02 },
        { x: -radius * 0.52, y: radius * 0.72 },
        { x: -radius * 0.75, y: -radius * 0.18 }
      ]
      g.fillStyle(visual.fill, 0.95)
      fillPolygon(g, outer)
      g.lineStyle(bold, visual.stroke, 0.95)
      strokePolygon(g, outer)
      g.lineStyle(stroke, THEME.white, 0.75)
      g.lineBetween(0, -radius * 0.9, 0, radius * 0.8)
      g.lineBetween(-radius * 0.55, -radius * 0.05, radius * 0.55, -radius * 0.05)
      g.lineBetween(-radius * 0.35, radius * 0.48, radius * 0.35, radius * 0.48)
      g.fillStyle(THEME.white, 0.4)
      fillPolygon(g, [
        { x: 0, y: -radius * 0.8 },
        { x: radius * 0.3, y: -radius * 0.22 },
        { x: 0, y: -radius * 0.02 },
        { x: -radius * 0.24, y: -radius * 0.28 }
      ])
      break
    }
    case 'circle':
    default: {
      // NORMAL — baseline mobile core (all other enemies derive complexity vs this)
      g.fillStyle(visual.fill, 0.96)
      g.fillCircle(0, 0, radius)
      g.lineStyle(bold, visual.stroke, 0.95)
      g.strokeCircle(0, 0, radius)
      g.fillStyle(THEME.white, 0.24)
      g.fillCircle(-radius * 0.28, -radius * 0.28, radius * 0.3)
      g.fillStyle(THEME.background, 0.35)
      g.fillCircle(0, 0, radius * 0.28)
      g.lineStyle(stroke, THEME.white, 0.35)
      g.strokeCircle(0, 0, radius * 0.28)
      break
    }
  }

  // ELITE — structural accent (orbit pip / shell facet), not gold border alone
  if (options?.elite) {
    g.lineStyle(stroke, THEME.white, 0.55)
    g.strokeCircle(0, 0, radius * 1.22)
    g.fillStyle(THEME.selection, 0.95)
    g.fillCircle(radius * 0.95, -radius * 0.55, radius * 0.14)
    g.lineStyle(stroke, THEME.white, 0.9)
    g.strokeCircle(radius * 0.95, -radius * 0.55, radius * 0.14)
    g.fillStyle(THEME.white, 0.85)
    g.fillCircle(-radius * 0.92, radius * 0.35, radius * 0.09)
  }

  // Active energy shield (break when depleted — separate from SHIELD family shell)
  if (options?.hasShield) {
    g.lineStyle(bold, THEME.shield, 0.95)
    g.strokeCircle(0, 0, radius * 1.32)
    g.lineStyle(stroke, THEME.white, 0.4)
    g.strokeCircle(0, 0, radius * 1.42)
  }

  if (options?.regenerating) {
    g.lineStyle(stroke, THEME.regenEnemy, 0.8)
    g.strokeCircle(0, 0, radius * 1.12)
    g.fillStyle(THEME.regenEnemy, 0.35)
    g.fillCircle(0, 0, radius * 0.18)
  }
}

export function paintProjectileVisual(
  g: Phaser.GameObjects.Graphics,
  visual: { fill: number; silhouette: string },
  radius: number,
  cellSize: number
): void {
  g.clear()
  const stroke = strokeWidthForCell(cellSize, 'hair')
  const thin = strokeWidthForCell(cellSize, 'thin')
  g.fillStyle(visual.fill, 1)
  g.lineStyle(stroke, THEME.white, 0.75)

  switch (visual.silhouette) {
    case 'crystal': {
      // FROST — faceted hex crystal (≠ diamond droplet / bolt)
      const outer = regularPolygon(0, 0, radius * 1.05, 6)
      fillPolygon(g, outer)
      strokePolygon(g, outer)
      g.fillStyle(THEME.white, 0.45)
      fillPolygon(g, regularPolygon(0, -radius * 0.12, radius * 0.42, 6))
      g.lineStyle(thin, THEME.white, 0.55)
      strokePolygon(g, diamondPoints(0, 0, radius * 0.28, radius * 0.55))
      break
    }
    case 'shell': {
      // CANNON — mass ball + bore wedge (≠ orb highlight alone)
      g.fillCircle(0, 0, radius)
      g.strokeCircle(0, 0, radius)
      g.fillStyle(0x0b1220, 0.55)
      g.fillCircle(0, 0, radius * 0.42)
      g.fillStyle(THEME.white, 0.4)
      g.fillCircle(-radius * 0.28, -radius * 0.28, radius * 0.28)
      const bore = [
        { x: radius * 0.15, y: -radius * 0.22 },
        { x: radius * 1.05, y: 0 },
        { x: radius * 0.15, y: radius * 0.22 }
      ]
      g.fillStyle(visual.fill, 1)
      fillPolygon(g, bore)
      g.lineStyle(stroke, THEME.white, 0.7)
      strokePolygon(g, bore)
      break
    }
    case 'droplet': {
      // POISON — teardrop tip + fat body (≠ crystal hex)
      const pts = [
        { x: 0, y: -radius * 1.2 },
        { x: radius * 0.55, y: -radius * 0.15 },
        { x: radius * 0.72, y: radius * 0.45 },
        { x: 0, y: radius * 0.95 },
        { x: -radius * 0.72, y: radius * 0.45 },
        { x: -radius * 0.55, y: -radius * 0.15 }
      ]
      fillPolygon(g, pts)
      strokePolygon(g, pts)
      g.fillStyle(THEME.white, 0.35)
      g.fillCircle(-radius * 0.12, radius * 0.15, radius * 0.22)
      break
    }
    case 'flare': {
      // FIRE — wide energy wedge + hot core (≠ AA dart)
      const pts = [
        { x: 0, y: -radius * 1.2 },
        { x: radius * 0.78, y: radius * 0.62 },
        { x: radius * 0.18, y: radius * 0.08 },
        { x: 0, y: radius * 0.28 },
        { x: -radius * 0.18, y: radius * 0.08 },
        { x: -radius * 0.78, y: radius * 0.62 }
      ]
      fillPolygon(g, pts)
      strokePolygon(g, pts)
      g.fillStyle(THEME.white, 0.5)
      fillPolygon(g, trianglePoints(0, -radius * 0.2, radius * 0.42))
      break
    }
    case 'dart': {
      // AA — slim piercing dart + fletching (≠ fire flare)
      const tip = [
        { x: 0, y: -radius * 1.25 },
        { x: radius * 0.32, y: radius * 0.05 },
        { x: 0, y: radius * 0.15 },
        { x: -radius * 0.32, y: radius * 0.05 }
      ]
      fillPolygon(g, tip)
      strokePolygon(g, tip)
      const finL = [
        { x: -radius * 0.08, y: radius * 0.05 },
        { x: -radius * 0.62, y: radius * 0.85 },
        { x: -radius * 0.05, y: radius * 0.45 }
      ]
      const finR = [
        { x: radius * 0.08, y: radius * 0.05 },
        { x: radius * 0.62, y: radius * 0.85 },
        { x: radius * 0.05, y: radius * 0.45 }
      ]
      g.fillStyle(visual.fill, 0.9)
      fillPolygon(g, finL)
      fillPolygon(g, finR)
      strokePolygon(g, finL)
      strokePolygon(g, finR)
      break
    }
    case 'bolt': {
      // MAGE — zigzag fracture bolt (≠ dart / flare)
      const pts = [
        { x: 0, y: -radius * 1.2 },
        { x: radius * 0.42, y: -radius * 0.35 },
        { x: radius * 0.12, y: -radius * 0.28 },
        { x: radius * 0.62, y: radius * 0.35 },
        { x: radius * 0.18, y: radius * 0.28 },
        { x: radius * 0.48, y: radius * 1.05 },
        { x: -radius * 0.05, y: radius * 0.2 },
        { x: radius * 0.08, y: radius * 0.12 },
        { x: -radius * 0.55, y: -radius * 0.45 },
        { x: -radius * 0.18, y: -radius * 0.38 }
      ]
      fillPolygon(g, pts)
      strokePolygon(g, pts)
      g.fillStyle(THEME.white, 0.4)
      g.fillCircle(0, -radius * 0.55, radius * 0.18)
      break
    }
    case 'orb':
    default: {
      g.fillCircle(0, 0, radius)
      g.strokeCircle(0, 0, radius)
      g.fillStyle(THEME.white, 0.35)
      g.fillCircle(-radius * 0.3, -radius * 0.25, radius * 0.3)
      break
    }
  }
}
